import React, { useState, useMemo, useEffect, FC } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Tab,
  Tabs,
  Paper,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Stack,
  Button,
} from '@mui/material';
import {
  Timeline,
  TrendingUp,
  Favorite,
  AccessTime,
  FilterList,
  Download,
  Refresh,
  MoreVert,
  MonitorHeart,
  ShowChart,
  Analytics,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { useAppDispatch, useAppSelector } from '~/hooks';
import { ppgActions } from '~/utils/store/slice';
import styles from './ppg.screen.module.scss';
import { PPGRecordResponseDTO } from '@kulak91/everbeat-api-client';
import { DataStatus } from '~/types/enums';

type StatsCardProps = {
  title: string;
  value: string | number;
  change?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info';
  isUp?: boolean;
};

const StatsCard: FC<StatsCardProps> = ({ title, value, change, color = 'primary', isUp = false }) => (
  <Card elevation={2} className={styles.statsCard}>
    <CardContent>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            {title}
          </Typography>
          <Typography variant='h4' component='div' color={`${color}.main`}>
            {value}
          </Typography>
          {change && (
            <Stack direction='row' alignItems='center' spacing={0.5}>
              {isUp && <TrendingUp fontSize='small' color='success' />}
              <Typography variant='caption' color='success.main'>
                {change}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

// Main waveform chart component
const WaveformChart = ({
  record,
  title,
  onExportRecord,
  height = 300,
}: {
  record: PPGRecordResponseDTO;
  title: string;
  onExportRecord: (record: PPGRecordResponseDTO) => void;
  height: number;
}) => {
  const chartData = useMemo(() => {
    if (!record || !record.waveforms.length) {
      return [];
    }

    const dataLength = record.waveforms.length;
    const sampleRate = dataLength > 5000 ? Math.ceil(dataLength / 2000) : 1;

    return record.waveforms
      .filter((_, index) => index % sampleRate === 0)
      .map((value, index) => ({
        time: index * sampleRate,
        value: value / 1000000,
        timestamp: index * sampleRate * (1000 / (record.sampleRate || 25)),
      }));
  }, [record]);

  return (
    <Card elevation={2} className={styles.waveformCard}>
      <CardContent>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
          <Typography variant='h6' component='div'>
            {title}
          </Typography>
          <Stack direction='row' spacing={1}>
            <Tooltip title='Download'>
              <IconButton size='small' className={styles.actionButton} onClick={() => onExportRecord(record)}>
                <Download />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ height, width: '100%', overflowX: 'auto' }} className={styles.chartContainer}>
          <Box
            sx={{ height: '100%', minWidth: Math.max(800, record.waveforms.length * 2) }}
            className={styles.chartBox}
          >
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                <XAxis dataKey='time' stroke='#666' fontSize={12} axisLine={false} tickLine={false} />
                <YAxis
                  stroke='#666'
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                  domain={['dataMin - 5', 'dataMax + 5']}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  }}
                  formatter={(value) => [Number(value).toFixed(2), 'PPG Signal']}
                  labelFormatter={(label) => `Sample: ${label}`}
                />
                <Area
                  type='monotone'
                  dataKey='value'
                  stroke='#1976d2'
                  strokeWidth={2}
                  fill='url(#gradient)'
                  fillOpacity={0.6}
                />
                <defs>
                  <linearGradient id='gradient' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#1976d2' stopOpacity={0.3} />
                    <stop offset='95%' stopColor='#1976d2' stopOpacity={0.1} />
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Individual record card component
const RecordCard = ({
  record,
  isSelected,
  onClick,
  handleExportRecord,
}: {
  record: PPGRecordResponseDTO;
  isSelected: boolean;
  onClick: () => void;
  handleExportRecord?: (record: PPGRecordResponseDTO) => void;
}) => {
  const duration = ((record.waveforms?.length || 0) / (record.sampleRate || 25)).toFixed(1);
  const avgSignal =
    record.waveforms?.length > 0
      ? (record.waveforms.reduce((a, b) => a + b, 0) / record.waveforms.length / 1000000).toFixed(2)
      : '0.00';

  return (
    <Card
      elevation={isSelected ? 4 : 1}
      className={`${styles.recordCard} ${isSelected ? styles.selected : ''}`}
      onClick={onClick}
    >
      <CardContent>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={1}>
          <Typography variant='subtitle2' color='text.secondary'>
            {new Date(record.createdAt).toLocaleDateString()}
          </Typography>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Tooltip title='Export Record'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleExportRecord?.(record);
                }}
              >
                <Download fontSize='small' />
              </IconButton>
            </Tooltip>
            <Chip label='Complete' size='small' color='success' variant='outlined' />
          </Stack>
        </Stack>

        <Typography variant='h6' gutterBottom>
          {new Date(record.createdAt).toLocaleTimeString()}
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <AccessTime fontSize='small' color='action' />
              <Typography variant='body2' color='text.secondary'>
                {duration}s
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <ShowChart fontSize='small' color='action' />
              <Typography variant='body2' color='text.secondary'>
                {record.sampleRate || 25} Hz
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Avg Signal: {avgSignal}
        </Typography>
      </CardContent>
    </Card>
  );
};

const daysAgo = (date: Date): number => {
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - date.getTime());

  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const daysAgoToColor = (days: number): 'primary' | 'secondary' | 'success' | 'error' | 'info' => {
  if (days <= 1) {
    return 'success';
  }

  if (days < 3) {
    return 'info';
  }

  if (days < 7) {
    return 'error';
  }

  return 'error';
};

const downloadJSON = (data: any, filename: string) => {
  const jsonContent = JSON.stringify(data, null, 2); // Pretty format with 2-space indent

  const blob = new Blob([jsonContent], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const PPGScreen = () => {
  const dispatch = useAppDispatch();
  const { loadPPGResultsStatus, ppgResults } = useAppSelector((state) => state.ppg);
  const { data } = useAppSelector((state) => state.user);

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState<PPGRecordResponseDTO | null>(null);
  // const [pagination, setPagination] = useState({ skip: 0, limit: 20, hasMore: true });
  // const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    dispatch(ppgActions.loadPPGResults({}));
  }, [dispatch]);

  useEffect(() => {
    if (ppgResults.length > 0 && !selectedRecord) {
      setSelectedRecord(ppgResults[0]);
    }
  }, [ppgResults, selectedRecord]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleRecordSelect = (record: PPGRecordResponseDTO) => {
    setSelectedRecord(record);
  };

  const handleRefresh = () => {
    // setLoading(true);
    dispatch(ppgActions.loadPPGResults({}));
    // setTimeout(() => setLoading(false), 1500);
  };

  const stats = {
    totalRecords: ppgResults.length,
    avgDuration:
      ppgResults.length > 0
        ? (
            ppgResults.reduce((acc, record) => acc + record.waveforms.length / (record.sampleRate || 25), 0) /
            ppgResults.length
          ).toFixed(1)
        : '0',
    lastReading: ppgResults[0] ? new Date(ppgResults[0].createdAt).toLocaleDateString() : 'N/A',
    daysAgo: ppgResults[0] ? daysAgo(new Date(ppgResults[0].createdAt)) : null,
  };

  const handleExportAllRecords = () => {
    const jsonData = {
      exportDate: new Date().toISOString(),
      totalRecords: ppgResults.length,
      patientId: data.patientId ?? 'unknown',
      records: ppgResults,
    };

    downloadJSON(jsonData, `ppg-all-records-${new Date().toISOString().split('T')[0]}.json`);
  };

  const handleExportRecord = (record: PPGRecordResponseDTO) => {
    const waveformData = record.waveforms.map((value) => ({
      rawValue: value,
    }));

    downloadCSV(waveformData, `ppg-waveform-${record.id}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const downloadCSV = (data: any[], filename: string) => {
    const headers = Object.keys(data[0] || {});
    const csvContent = [headers.join(','), ...data.map((row) => headers.map((header) => row[header]).join(','))].join(
      '\n',
    );

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box className={styles.ppgScreen}>
      {/* Header */}
      <div className={styles.header}>
        <Stack direction='row' alignItems='center' spacing={2}>
          <Avatar className={styles.headerIcon}>
            <MonitorHeart fontSize='large' />
          </Avatar>
          <Stack>
            <Typography variant='h4' component='h1' className={styles.headerTitle}>
              PPG Analytics
            </Typography>
            <Typography variant='body2' color='text.secondary' className={styles.headerSubtitle}>
              Photoplethysmography Signal Analysis • Status: {loadPPGResultsStatus} • Records: {ppgResults.length}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<FilterList />}
            onClick={handleExportAllRecords}
            className={styles.headerButton}
            disabled={!ppgResults.length}
          >
            Export All Records (JSON)
          </Button>
          <Button variant='contained' startIcon={<Refresh />} onClick={handleRefresh} className={styles.headerButton}>
            Refresh
          </Button>
        </Stack>
      </div>

      {loadPPGResultsStatus === DataStatus.LOADING && <LinearProgress className={styles.loadingBar} />}

      {/* Stats Overview */}
      <Grid container spacing={3} className={styles.statsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard title='Total Records' value={stats.totalRecords} color='primary' />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard title='Avg Duration' value={`${stats.avgDuration}s`} color='info' />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard title='Last Reading' value={stats.lastReading} color={daysAgoToColor(stats.daysAgo ?? 10)} />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper elevation={0} className={styles.mainContent}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          className={styles.tabs}
          indicatorColor='primary'
          textColor='primary'
        >
          <Tab label='Real-time Analysis' />
          <Tab label='Historical Data' />
          <Tab label='Reports & Insights' />
        </Tabs>
        <Divider />

        <Box className={styles.tabContent}>
          {selectedTab === 0 && (
            <Grid container spacing={3}>
              {/* Waveform Visualization */}
              <Grid item xs={12} lg={8}>
                {selectedRecord ? (
                  <WaveformChart
                    onExportRecord={handleExportRecord}
                    record={selectedRecord}
                    title={`PPG Waveform - ${new Date(selectedRecord.createdAt).toLocaleString()}`}
                    height={400}
                  />
                ) : (
                  <Alert severity='info' className={styles.noDataAlert}>
                    No PPG data available. Please wait for data to load or check your connection.
                  </Alert>
                )}
              </Grid>

              {/* Record List */}
              <Grid item xs={12} lg={4}>
                <Card elevation={2} className={styles.recordsPanel}>
                  <CardContent>
                    <Typography variant='h6' gutterBottom>
                      Recent Recordings ({ppgResults.length})
                    </Typography>
                    <div className={styles.recordsList}>
                      {ppgResults.map((record) => (
                        <RecordCard
                          key={record.id}
                          record={record}
                          isSelected={selectedRecord?.id === record.id}
                          onClick={() => handleRecordSelect(record)}
                          handleExportRecord={handleExportRecord}
                        />
                      ))}
                      {ppgResults.length === 0 && (
                        <Typography variant='body2' color='text.secondary' className={styles.emptyState}>
                          No recordings available. Data will appear here once loaded.
                        </Typography>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {selectedTab === 1 && (
            <Box>
              <Alert severity='info' className={styles.infoAlert}>
                Historical data analysis showing trends and patterns over time.
              </Alert>
              <Grid container spacing={3}>
                {ppgResults.slice(0, 4).map((record) => (
                  <Grid item xs={12} md={6} key={record.id}>
                    <WaveformChart
                      onExportRecord={handleExportRecord}
                      record={record}
                      title={`Historical Record - ${new Date(record.createdAt).toLocaleString()}`}
                      height={250}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {selectedTab === 2 && (
            <Box>
              <Alert severity='info' className={styles.infoAlert}>
                Comprehensive health insights and statistical analysis based on your PPG data.
              </Alert>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {selectedRecord && (
                    <WaveformChart
                      onExportRecord={handleExportRecord}
                      record={selectedRecord}
                      title='Detailed Signal Analysis'
                      height={300}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export { PPGScreen };
